import { ETrafficVolume } from '@safesystem/client-playbook-lib';
import type { Corridor, Segment, Project } from '#data/safesystem/safecurves/contract.js';
import { aadtFromLabel } from '../Countermeasures/DataAttributes.svelte';
import { allUnpaved, isP1orP2, isPaved } from './recommendedLength';

type Sheeting = NonNullable<Segment['signSheetingType']>;
type SheetingCorridor = Pick<Corridor, 'roadSeal' | 'priorityRating' | 'aadtBand'>;

// Unpaved Priority 1/2 roads at mid or high volume get high-retro sheeting whatever the crashes; an unknown band doesn't.
export function highRetroRoad(c: SheetingCorridor): boolean {
	const volume = aadtFromLabel(c.aadtBand);
	return (
		!isPaved(c) &&
		isP1orP2(c.priorityRating) &&
		(volume === ETrafficVolume.Mid || volume === ETrafficVolume.High)
	);
}

// Wet/dark crashes only count on a project with a Priority 1 or 2 corridor; elsewhere the question is hidden.
export const wetdarkApplies = (corridors: Pick<Corridor, 'priorityRating'>[]) =>
	corridors.some((c) => isP1orP2(c.priorityRating));

// The wet/dark band the recommendations use: the recorded one where it applies, else the baseline.
export const effectiveWetdark = (
	crashesWetdark: Project['crashesWetdark'],
	corridors: Pick<Corridor, 'priorityRating'>[]
): Project['crashesWetdark'] => (wetdarkApplies(corridors) ? crashesWetdark : '0-1');

// Ask about wet/dark crashes only when the answer changes something: paint is offered, or some road's sheeting follows it.
export const wetdarkAsked = (corridors: SheetingCorridor[]) =>
	wetdarkApplies(corridors) && (!allUnpaved(corridors) || corridors.some((c) => !highRetroRoad(c)));

/** Why high-retro sheeting is recommended on a segment of `corridor`: the road itself, wet/dark crashes, or not at all. */
export function sheetingReason(
	crashesWetdark: Project['crashesWetdark'],
	corridor?: SheetingCorridor
): 'road' | 'wetdark' | null {
	if (corridor && highRetroRoad(corridor)) return 'road';
	return crashesWetdark === '0-1' ? null : 'wetdark';
}

export const recommendedSheeting = (
	crashesWetdark: Project['crashesWetdark'],
	corridor?: SheetingCorridor
): Sheeting => (sheetingReason(crashesWetdark, corridor) ? 'High retroreflective' : 'Standard');

// A null segment sheeting follows the recommendation for its corridor; only a user choice stores a value.
export const resolveSheeting = (
	segment: Pick<Segment, 'signSheetingType'>,
	crashesWetdark: Project['crashesWetdark'],
	corridor?: SheetingCorridor
): Sheeting => segment.signSheetingType ?? recommendedSheeting(crashesWetdark, corridor);

// The same sign/plaque countermeasure code in the other sheeting variant (see the countermeasure constants).
export function signCodeFor(code: string, highRetro: boolean): string {
	const base = code.replace(/-(std|high-retro)$/, '');
	if (base === 'new-sign') return highRetro ? 'new-sign-high-retro' : 'new-sign-std';
	if (base === 'chevron') return highRetro ? 'chevron-high-retro' : 'chevron-std';
	return highRetro ? `${base}-high-retro` : base;
}

export const isChevron = (s: { advisorySign: string | null }) =>
	(s.advisorySign ?? '').includes('Chevron');
